/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedin, faReddit } from "@fortawesome/free-brands-svg-icons";

import { environment } from "../../environments/environment";
import { useAuthContext } from "../../hooks/useAuthContext";
import PostModal from "../../components/PostModal/PostModal";
import axios from "axios";
import "../Dashboard/Dashboard.css";
import "./Recent.css";

function Recent() {
  document.title = `Leads today | LeadZLab`;

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLeadId, setExpandedLeadId] = useState(null);
  const { user } = useAuthContext();

  const fetchTodayLeads = async () => {
    if (!user?.accessToken) return;
    setLoading(true);
    try {
      const now = new Date();
      const todayStr = now.getFullYear() + "-" + String(now.getMonth() + 1).padStart(2, "0") + "-" + String(now.getDate()).padStart(2, "0");
      const tzOffsetMinutes = now.getTimezoneOffset();
      const params = new URLSearchParams({
        limit: "100",
        today_only: "true",
        todayDate: todayStr,
        tzOffsetMinutes: String(tzOffsetMinutes),
      });
      const { data } = await axios.get(
        `${environment.BACKEND}/resources/dashboard/recent-leads?${params}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user?.accessToken}`,
          },
        }
      );
      setLeads(data.data?.leads || []);
    } catch (err) {
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodayLeads();
  }, [user?.accessToken]);

  useEffect(() => {
    if (!expandedLeadId) return;
    const onKey = (e) => {
      if (e.key === "Escape") setExpandedLeadId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expandedLeadId]);

  const getRelativeTime = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(Date.parse(dateString));
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    return new Date(dateString).toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const markLeadRead = async (postId) => {
    if (!postId || !user?.accessToken) return;
    try {
      await axios.put(
        `${environment.BACKEND}/resources/post/read-status`,
        { postIds: [postId], is_read: true },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${user?.accessToken}` } }
      );
      setLeads((prev) => prev.map((p) => (p._id === postId ? { ...p, is_read: true } : p)));
    } catch (err) {
      // ignore
    }
  };

  return (
    <div className="recent-page">
      <header className="recent-header">
        <h1 className="recent-title">Leads today</h1>
        <p className="recent-subtitle">Leads found in the last 24 hours.</p>
        <NavLink to="/dashboard" className="recent-back-link">← Dashboard</NavLink>
      </header>

      {loading ? (
        <div className="recent-list-wrap rounded-xl border border-slate-200 bg-white p-4">
          <Skeleton count={6} height={56} className="rounded-lg" />
        </div>
      ) : leads.length === 0 ? (
        <div className="recent-empty rounded-xl border border-slate-200 bg-slate-50/50 p-8 text-center">
          <p className="text-slate-600">No leads found today yet.</p>
          <NavLink to="/jobs" className="mt-3 inline-block text-sm font-medium text-emerald-600 hover:text-emerald-700">
            View scouts →
          </NavLink>
        </div>
      ) : (
        <div className="recent-list-wrap rounded-xl border border-slate-200 bg-white overflow-hidden">
          <ul className="dashboard-leads-list">
            {leads.map((lead) => {
              const isLinkedIn = lead.type === "linkedin";
              const isUnread = !lead.is_read;
              const timeAgo = getRelativeTime(lead.post_created_at);
              const subLabel = !isLinkedIn && lead.subreddit_name
                ? (lead.subreddit_name.startsWith("r/") ? lead.subreddit_name : `r/${lead.subreddit_name}`)
                : null;
              const viewUrl = lead.postLink || lead.linkedInProfileURL || "#";
              return (
                <li
                  key={lead._id}
                  className={`dashboard-lead-row ${isUnread ? "dashboard-lead-row-unread" : "dashboard-lead-row-read"} dashboard-lead-row-${lead.type || "reddit"}`}
                  onClick={() => {
                    setExpandedLeadId(lead._id);
                    if (!lead.is_read) markLeadRead(lead._id);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setExpandedLeadId(lead._id);
                      if (!lead.is_read) markLeadRead(lead._id);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={isLinkedIn ? `View ${lead.user || "lead"}` : `View ${lead.postTitle || "lead"}`}
                >
                  <div className="dashboard-lead-row-inner">
                    <span className="dashboard-lead-dot-slot">
                      {isUnread ? <span className="dashboard-lead-dot" aria-hidden /> : null}
                    </span>
                    <div className="dashboard-lead-main">
                      {isLinkedIn ? (
                        <>
                          <div className="dashboard-lead-title-line">
                            <span className={`dashboard-lead-title ${isUnread ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`} title={lead.user || "—"}>
                              {lead.user || "—"}
                            </span>
                            {isUnread && <span className="dashboard-lead-pill-new">NEW</span>}
                          </div>
                          {lead.bodySnippet && (
                            <p className="dashboard-lead-preview line-clamp-2" title={lead.bodySnippet}>
                              {lead.bodySnippet}{lead.bodySnippet.length >= 120 ? "…" : ""}
                            </p>
                          )}
                        </>
                      ) : (
                        <div className="dashboard-lead-title-line">
                          <span className={`dashboard-lead-title ${isUnread ? "font-semibold text-slate-900" : "font-medium text-slate-700"}`} title={lead.postTitle || lead.user || "Lead"}>
                            {lead.postTitle || lead.user || "Lead"}
                          </span>
                          {isUnread && <span className="dashboard-lead-pill-new">NEW</span>}
                        </div>
                      )}
                      <span className="dashboard-lead-meta">
                        <span className="dashboard-lead-source-icon" title={isLinkedIn ? "LinkedIn" : "Reddit"}>
                          <FontAwesomeIcon icon={isLinkedIn ? faLinkedin : faReddit} className={isLinkedIn ? "dashboard-lead-icon-linkedin" : "dashboard-lead-icon-reddit"} />
                        </span>
                        {isLinkedIn ? (
                          <><span>{lead.jobName}</span><span className="dashboard-lead-meta-sep">·</span><span className="dashboard-lead-date">{timeAgo}</span></>
                        ) : (
                          <>
                            {subLabel && <span>{subLabel}</span>}
                            {subLabel && <span className="dashboard-lead-meta-sep">·</span>}
                            <span>{lead.jobName}</span>
                            <span className="dashboard-lead-meta-sep">·</span>
                            <span className="dashboard-lead-date">{timeAgo}</span>
                          </>
                        )}
                      </span>
                    </div>
                    <div className="dashboard-lead-actions" onClick={(e) => e.stopPropagation()}>
                      <a
                        href={viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="dashboard-lead-btn dashboard-lead-btn-view"
                      >
                        View
                      </a>
                      {isUnread && (
                        <button
                          type="button"
                          onClick={() => markLeadRead(lead._id)}
                          className="dashboard-lead-btn dashboard-lead-btn-markread"
                        >
                          Mark read
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <div className="dashboard-leads-list-footer">
            <NavLink to="/jobs" className="dashboard-leads-list-link">
              View all scouts →
            </NavLink>
          </div>
        </div>
      )}

      {expandedLeadId && (() => {
        const lead = leads.find((l) => l._id === expandedLeadId);
        if (!lead) return null;
        return (
          <PostModal
            post={lead}
            onClose={() => setExpandedLeadId(null)}
            isLinkedIn={lead.type === "linkedin"}
            showAiOutreach={false}
            accessToken={user?.accessToken}
            onMarkRead={markLeadRead}
          />
        );
      })()}
    </div>
  );
}

export default Recent;
